import React, { useState, useEffect, useRef } from 'react';
import UsaHeader from '../components/UsaHeader';
import UsaFooter from '../components/UsaFooter';
import UsaWhoWeAre from '../components/UsaWhoWeAre';
import UsaOnlineCourses from '../components/UsaOnlineCourses';
import UsaFreeAssessment from '../components/UsaFreeAssessment';
import UsaWhyChooseUs from '../components/UsaWhyChooseUs';
import UsaHowItWorks from '../components/UsaHowItWorks';
import UsaDetailedCourses from '../components/UsaDetailedCourses';
import UsaKidsClasses from '../components/UsaKidsClasses';
import UsaPerksBenefits from '../components/UsaPerksBenefits';
import UsaAboutFeatures from '../components/UsaAboutFeatures';
import UsaTestimonials from '../components/UsaTestimonials';
import UsaAdultClasses from '../components/UsaAdultClasses';
import UsaContactSection from '../components/UsaContactSection';
import FreeTrialModal from '../components/FreeTrialModal';
import { submitForm } from '../utils/formSubmit';
import Swal from 'sweetalert2';
import { FiArrowRight } from 'react-icons/fi';
import './Usa.css';
import CertifiedTutors from '../components/CertifiedTutors';
import PricingAndStates from '../components/PricingAndStates';
import FAQSection from '../components/FAQSection';

// Embedded inline SVG US Flag (100% reliable, zero network dependency)
const UsFlag = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 650 390"
    className="usa-hero__flag-img"
    style={{ borderRadius: '2px', display: 'inline-block', verticalAlign: 'middle', width: '22px', height: '14px', flexShrink: 0 }}
  >
    <rect width="650" height="390" fill="#b22234" />
    <path d="M0 30h650M0 90h650M0 150h650M0 210h650M0 270h650M0 330h650" stroke="#fff" strokeWidth="30" />
    <rect width="260" height="210" fill="#3c3b6e" />
    <g fill="#fff">
      <circle cx="26" cy="21" r="9" />
      <circle cx="78" cy="21" r="9" />
      <circle cx="130" cy="21" r="9" />
      <circle cx="182" cy="21" r="9" />
      <circle cx="234" cy="21" r="9" />

      <circle cx="52" cy="52.5" r="9" />
      <circle cx="104" cy="52.5" r="9" />
      <circle cx="156" cy="52.5" r="9" />
      <circle cx="208" cy="52.5" r="9" />

      <circle cx="26" cy="84" r="9" />
      <circle cx="78" cy="84" r="9" />
      <circle cx="130" cy="84" r="9" />
      <circle cx="182" cy="84" r="9" />
      <circle cx="234" cy="84" r="9" />

      <circle cx="52" cy="115.5" r="9" />
      <circle cx="104" cy="115.5" r="9" />
      <circle cx="156" cy="115.5" r="9" />
      <circle cx="208" cy="115.5" r="9" />

      <circle cx="26" cy="147" r="9" />
      <circle cx="78" cy="147" r="9" />
      <circle cx="130" cy="147" r="9" />
      <circle cx="182" cy="147" r="9" />
      <circle cx="234" cy="147" r="9" />

      <circle cx="52" cy="178.5" r="9" />
      <circle cx="104" cy="178.5" r="9" />
      <circle cx="156" cy="178.5" r="9" />
      <circle cx="208" cy="178.5" r="9" />
    </g>
  </svg>
);

const countryList = [
  {
    name: 'United States',
    code: '+1',
    iso: 'us'
  }
];

const Usa = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    course: '',
    phone: '',
    message: ''
  });

  const [selectedCountry, setSelectedCountry] = useState(countryList[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', 'noindex, nofollow');

    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitForm('USA Page Hero Form', {
        fullName: `${formData.firstName} ${formData.lastName}`.trim(),
        email: 'Not Provided (Hero Form)',
        phone: `${selectedCountry.code} ${formData.phone}`,
        course: formData.course,
        message: formData.message
      });
      Swal.fire({
        title: 'Trial Booked!',
        text: `Thank you, ${formData.firstName}! Your 3-Day Free Trial request has been submitted.`,
        icon: 'success',
        confirmButtonColor: '#006100'
      });
      setFormData({
        firstName: '',
        lastName: '',
        course: '',
        phone: '',
        message: ''
      });
      window.location.href = '/thank-you-enrollment-successful/';
    } catch (error) {
      Swal.fire({
        title: 'Submission Error',
        text: 'Failed to submit form. Please try again.',
        icon: 'error',
        confirmButtonColor: '#006100'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCountries = countryList.filter(
    (c) =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.code.includes(searchQuery)
  );

  return (
    <div className="usa-page">
      {/* Custom Header */}
      <UsaHeader activeTab="home" onOpenModal={() => setIsModalOpen(true)} />

      {/* Main Content Area */}
      <main className="usa-main-content">
        {/* Hero Section */}
        <section
          className="usa-hero"
          style={{
            backgroundImage: `linear-gradient(rgba(75, 143, 68, 0.88), rgba(75, 143, 68, 0.88)), url('${process.env.PUBLIC_URL}/assets/images/Hero-5.jpg')`
          }}
        >
          <div className="usa-hero__overlay"></div>
          <div className="usa-hero__container">
            {/* Left Content Column */}
            <div className="usa-hero__left">
              <div className="usa-hero__guarantee-badge">
                <svg className="usa-hero__badge-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-3zm-1 14.5l-3.5-3.5 1.41-1.41L11 13.67l5.09-5.09 1.41 1.41L11 16.5z" />
                </svg>
                <span>100% Satisfaction Guarantee</span>
              </div>

              <h1 className="usa-hero__title">
                Online Quran Classes for Kids & Adults in the USA – Live, One-on-One & Certified
              </h1>

              <p className="usa-hero__description">
                Al Quran Lesson connects Muslim families across the United States with certified, native Arabic-speaking tutors for structured, one-on-one online Quran classes for kids and adults on your schedule, with zero compromise on quality. Trusted since 2013. 5,000+ students enrolled. Book your 3-day free trial now without providing a card and start learning the Quran at your ease.
              </p>

              <div className="usa-hero__cta">
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="usa-hero__cta-btn"
                  style={{ border: 'none', cursor: 'pointer' }}
                >
                  Claim Your Free Trial <FiArrowRight className="usa-hero__cta-arrow" />
                </button>
              </div>
            </div>

            {/* Right Form Card Column */}
            <div className="usa-hero__right" id="hero-form">
              <div className="usa-hero__form-card">
                <h2 className="usa-hero__form-header">
                  No Credit Card Required · 3-Day Free Trial · Cancel Anytime
                </h2>

                <form onSubmit={handleSubmit} className="usa-hero__form">
                  <div className="usa-hero__form-row">
                    <input
                      type="text"
                      name="firstName"
                      placeholder="First Name"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="usa-hero__input"
                    />
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Last Name"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="usa-hero__input"
                    />
                  </div>

                  <div className="usa-hero__form-group">
                    <select
                      name="course"
                      value={formData.course}
                      onChange={handleChange}
                      required
                      className="usa-hero__select"
                    >
                      <option value="" disabled hidden>Select Courses</option>
                      <option value="Noorani Qaida">Noorani Qaida</option>
                      <option value="Quran Tajweed Classes">Quran Tajweed Classes</option>
                      <option value="Quran Reading Classes">Quran Reading Classes</option>
                      <option value="Quran Memorization Classes">Quran Memorization Classes</option>
                      <option value="Quran Translation Classes">Quran Translation Classes</option>
                    </select>
                  </div>

                  {/* Interactive Phone Field with Embedded US Flag SVG */}
                  <div className="usa-hero__form-group" ref={dropdownRef} style={{ position: 'relative' }}>
                    <div className="usa-hero__phone-box">
                      <button
                        type="button"
                        className="usa-hero__country-picker-btn"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      >
                        <span className="usa-hero__country-flag">
                          <UsFlag />
                        </span>
                        <span className="usa-hero__country-arrow">▲</span>
                        <span className="usa-hero__country-code">{selectedCountry.code}</span>
                      </button>
                      <span className="usa-hero__phone-divider">|</span>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="Phone Number"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="usa-hero__phone-field"
                      />
                    </div>

                    {/* Country Code Dropdown */}
                    {isDropdownOpen && (
                      <div className="usa-hero__country-dropdown">
                        <div className="usa-hero__search-wrapper">
                          <input
                            type="text"
                            placeholder="Search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="usa-hero__search-input"
                            autoFocus
                          />
                        </div>
                        <ul className="usa-hero__country-menu">
                          {filteredCountries.map((c) => (
                            <li
                              key={c.name}
                              className={`usa-hero__country-option ${c.name === selectedCountry.name ? 'usa-hero__country-option--active' : ''}`}
                              onClick={() => {
                                setSelectedCountry(c);
                                setIsDropdownOpen(false);
                                setSearchQuery('');
                              }}
                            >
                              <span className="usa-hero__opt-flag">
                                <UsFlag />
                              </span>
                              <span className="usa-hero__opt-name">{c.name}</span>
                              <span className="usa-hero__opt-code">{c.code}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="usa-hero__form-group">
                    <input
                      type="text"
                      name="message"
                      placeholder="Message"
                      value={formData.message}
                      onChange={handleChange}
                      className="usa-hero__input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="usa-hero__submit-btn"
                  >
                    {submitting ? 'Booking Trial...' : 'Book My Free Trial'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Card Overlapping Banner */}
        <section className="usa-stats-banner">
          <div className="usa-stats-banner__container">
            <div className="usa-stats-banner__card">
              <div className="usa-stats-banner__grid">
                {/* Stat Item 1 */}
                <div className="usa-stat-box">
                  <div className="usa-stat-box__number">20+</div>
                  <div className="usa-stat-box__hearts">
                    ❤️ ❤️ ❤️ ❤️
                  </div>
                  <div className="usa-stat-box__label">Certified Tutors</div>
                </div>

                {/* Stat Item 2 */}
                <div className="usa-stat-box">
                  <div className="usa-stat-box__number">4.6</div>
                  <div className="usa-stat-box__stars">
                    ⭐ ⭐ ⭐ ⭐ ⭐
                  </div>
                  <div className="usa-stat-box__label">Reviews</div>
                </div>

                {/* Stat Right Message */}
                <h3 className="usa-stats-banner__text">
                  Trusted by <strong>5,000+ happy students</strong> that are joining with us to achieve their goal!
                </h3>
              </div>
            </div>
          </div>
        </section>

        {/* USA Dedicated Who We Are Section (with rotating certified stamp on scroll) */}
        <UsaWhoWeAre onOpenModal={() => setIsModalOpen(true)} />

        {/* USA Online Quran Courses Section matching screenshots */}
        <UsaOnlineCourses />

        {/* USA Free Registration Assessment Section matching screenshot */}
        <UsaFreeAssessment />

        {/* USA Why Choose Us Section matching screenshot */}
        <UsaWhyChooseUs />

        {/* USA How It Works Section matching screenshot */}
        <UsaHowItWorks />

        {/* USA Detailed Quran Courses Section matching screenshot & user data */}
        <UsaDetailedCourses />

        {/* USA Kids Quran Classes Section matching screenshot */}
        <UsaKidsClasses onOpenModal={() => setIsModalOpen(true)} />

        {/* USA Perks and Benefits Section matching screenshot */}
        <UsaPerksBenefits />

        {/* USA About Features Section matching screenshot (6 features + Arch image + CTA) */}
        <UsaAboutFeatures onOpenModal={() => setIsModalOpen(true)} />

        {/* USA Testimonials Section matching screenshot (Their Stories Speak For Us) */}
        <UsaTestimonials />

        {/* USA Online Quran Classes For Adults Section matching screenshot (No internal links) */}
        <UsaAdultClasses onOpenModal={() => setIsModalOpen(true)} />


      </main>

      <CertifiedTutors useContactLink={true} />
      <PricingAndStates defaultCurrency="USD" onOpenModal={() => setIsModalOpen(true)} isUsaPage={true} />
      <FAQSection isUsaPage={true} />

      {/* USA Contact Section matching screenshot (Let's Talk About Your Quran Classes Online) */}
      <UsaContactSection />

      {/* Custom Footer */}
      <UsaFooter />

      {/* Free Trial Modal matching screenshot */}
      <FreeTrialModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default Usa;
